// utils/convertToLexicalHtml.js
import { createEditor } from "lexical";
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { $getRoot, $insertNodes } from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { marked } from "marked";
import { normalizeLatexToDoubleDollar } from "./fileHelpers";
import { MathNode } from "../../../../../../components/dashboard/teacher/lessons/MathNode";
import { ImageNode } from "../../../../../../components/dashboard/teacher/lessons/ImageNode";
import { IframeNode } from "../../../../../../components/dashboard/teacher/lessons/IframeNode";
import { YouTubeTimestampNode } from "../../../../../../components/dashboard/teacher/lessons/YouTubeTimestampNode";
import exampleTheme from "../../../../../../components/editor/themes/ExampleTheme";

/**
 * Converts raw markdown/HTML (as received from WebSocket) into
 * Lexical-processed HTML — matching exactly what UpdatePlugin produces.
 *
 * @param {string} rawAnswer - Raw markdown or HTML string from WebSocket
 * @returns {Promise<string>} - Lexical-serialized HTML string
 */
export async function convertToLexicalHtml(rawAnswer) {
    return new Promise((resolve, reject) => {
        const editor = createEditor({
            theme: exampleTheme,
            nodes: [
                HeadingNode,
                ListNode,
                ListItemNode,
                QuoteNode,
                CodeNode,
                CodeHighlightNode,
                TableNode,
                TableCellNode,
                TableRowNode,
                AutoLinkNode,
                LinkNode,
                MathNode,
                ImageNode,
                // keep in sync with the main editor config (NewLessonContext) — unregistered
                // <iframe>/<button> elements are silently dropped by $generateNodesFromDOM
                IframeNode,
                YouTubeTimestampNode,
            ],
            onError: reject,
        });


        editor.update(
            () => {
                try {
                    const parser = new DOMParser();

                    // Step 1: Normalize LaTeX and split on $$
                    const preprocessedText = normalizeLatexToDoubleDollar(rawAnswer);
                    const sections = preprocessedText?.split(/\$\$/) || [];

                    const processedSections = sections.map((section, index) => {
                        if (index % 2 === 1) {
                            if (!section || section.trim() === "") return "";
                            const escapedSection = section
                                .replace(/</g, " \\lt ")
                                .replace(/>/g, " \\gt ");
                            return `<p>$$${escapedSection}$$</p>`;
                        }
                        return marked(section);
                    });

                    let htmlContent = processedSections.join("");
                    htmlContent = htmlContent
                        .replace("<STARTOFTURN>", "")
                        .replace("<ENDOFTURN>", "");

                    // Step 2: Parse into Lexical nodes
                    const dom = parser.parseFromString(htmlContent, "text/html");
                    const nodes = $generateNodesFromDOM(editor, dom);

                    const root = $getRoot();
                    root.clear();
                    $insertNodes(nodes);
                } catch (err) {
                    reject(err);
                }
            },
            { discrete: true }
        );

        // Step 3: Read back the Lexical-serialized HTML
        editor.getEditorState().read(() => {
            try {
                const html = $generateHtmlFromNodes(editor, null);
                resolve(html);
            } catch (err) {
                reject(err);
            }
        });
    });
}